// apps/client/src/hooks/useConsentTexts.ts
//
// Consent wording registry (Lead Capture > Consent). One callable,
// `consentTextAction` (client-functions consent/registry.ts, workstream D,
// super_admin only):
//
//   listTexts   -> { texts }
//   createDraft { draft: { purpose, text, label } } -> { versionId, sha256 }
//               server computes the sha256 and the version id
//   activate    -> { versionId, purpose, retired } retires the previous active
//                               text of that purpose in the same transaction
//   retire      -> { versionId, purpose }
//
// An active text is immutable: changing wording means a new draft, then
// activating it. Nothing here edits a text in place.
//
// Spec: docs/plans/2026-10-23-WEBSITE-FORMS-PLATFORM.md §2.3, §5 and §9.

import { useCallback, useEffect, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
// Sub-path import: consent.ts is browser-safe (no crypto); the barrel is not.
import type { ConsentPurpose, ConsentTextStatus } from '@valutten/shared-schemas/dist/consent';
import { functions } from '../lib/firebase';
import { callableErrorMessage, timestampToMillis } from './useWebsiteConnections';

export const CONSENT_TEXT_CALLABLE = 'consentTextAction';

/** A consent text as the callable returns it (timestamps in any serialised shape). */
export interface ConsentTextView {
  versionId: string;
  purpose: ConsentPurpose;
  text: string;
  sha256: string;
  status: ConsentTextStatus;
  label: string;
  createdAt: unknown;
  createdBy: string;
  activatedAt: unknown;
  activatedBy: string | null;
  retiredAt: unknown;
}

type Action =
  | { action: 'listTexts'; companyId: string }
  | { action: 'createDraft'; companyId: string; draft: { purpose: ConsentPurpose; text: string; label: string } }
  | { action: 'activate'; companyId: string; versionId: string }
  | { action: 'retire'; companyId: string; versionId: string };

async function call<T>(payload: Action): Promise<T> {
  const fn = httpsCallable<Action, T>(functions, CONSENT_TEXT_CALLABLE);
  const res = await fn(payload);
  return res.data;
}

/** One purpose's texts, split the way the screen shows them. */
export interface ConsentPurposeGroup {
  active: ConsentTextView | null;
  /** Newest first. */
  drafts: ConsentTextView[];
  /** Retired texts, most recently retired first. */
  history: ConsentTextView[];
}

/**
 * Group texts per purpose. If the stored data ever breaks the "one active per
 * purpose" invariant, the most recently activated text is shown as active and
 * the others are listed in history, so the screen never hides a live text.
 */
export function groupConsentTexts(
  texts: readonly ConsentTextView[],
  purposes: readonly ConsentPurpose[],
): Record<ConsentPurpose, ConsentPurposeGroup> {
  const out = {} as Record<ConsentPurpose, ConsentPurposeGroup>;
  for (const p of purposes) out[p] = { active: null, drafts: [], history: [] };
  const newest = (a: unknown, b: unknown) => (timestampToMillis(b) ?? 0) - (timestampToMillis(a) ?? 0);
  const sorted = texts.slice();
  for (const t of sorted) {
    const g = out[t.purpose];
    if (!g) continue;
    if (t.status === 'draft') g.drafts.push(t);
    else if (t.status === 'retired') g.history.push(t);
  }
  for (const p of purposes) {
    const actives = sorted
      .filter((t) => t.purpose === p && t.status === 'active')
      .sort((a, b) => newest(a.activatedAt ?? a.createdAt, b.activatedAt ?? b.createdAt));
    out[p].active = actives[0] ?? null;
    out[p].history.push(...actives.slice(1));
    out[p].drafts.sort((a, b) => newest(a.createdAt, b.createdAt));
    out[p].history.sort((a, b) => newest(a.retiredAt ?? a.activatedAt ?? a.createdAt, b.retiredAt ?? b.activatedAt ?? b.createdAt));
  }
  return out;
}

export interface UseConsentTextsResult {
  texts: ConsentTextView[];
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Each write rejects with a user-facing Error; on success the list reloads. */
  createDraft: (input: { purpose: ConsentPurpose; text: string; label: string }) => Promise<void>;
  activate: (versionId: string) => Promise<void>;
  retire: (versionId: string) => Promise<void>;
}

export function useConsentTexts(companyId: string | undefined): UseConsentTextsResult {
  const [texts, setTexts] = useState<ConsentTextView[]>([]);
  const [loading, setLoading] = useState<boolean>(Boolean(companyId));
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!companyId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    call<{ texts?: ConsentTextView[] }>({ action: 'listTexts', companyId })
      .then((data) => {
        if (!cancelled) setTexts(Array.isArray(data?.texts) ? data.texts : []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[useConsentTexts] listTexts failed', err);
        setError(callableErrorMessage(err, 'Consent wording could not be loaded. Refresh to try again.'));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [companyId, refreshKey]);

  // Writes reload the whole list: activate changes two texts at once (the new
  // active and the one it retires), so patching one row locally would show a
  // second active text until the next refresh.
  const write = useCallback(
    async (payload: Action, fallback: string) => {
      if (!companyId) throw new Error('No company is selected.');
      try {
        await call<unknown>(payload);
      } catch (err) {
        throw new Error(callableErrorMessage(err, fallback));
      }
      refresh();
    },
    [companyId, refresh],
  );

  const createDraft = useCallback(
    (input: { purpose: ConsentPurpose; text: string; label: string }) =>
      write({ action: 'createDraft', companyId: companyId ?? '', draft: input }, 'The draft was not saved. Please try again.'),
    [companyId, write],
  );

  const activate = useCallback(
    (versionId: string) =>
      write({ action: 'activate', companyId: companyId ?? '', versionId }, 'The wording was not activated. Please try again.'),
    [companyId, write],
  );

  const retire = useCallback(
    (versionId: string) =>
      write({ action: 'retire', companyId: companyId ?? '', versionId }, 'The wording was not retired. Please try again.'),
    [companyId, write],
  );

  return { texts, loading, error, refresh, createDraft, activate, retire };
}
